import { ScoredLoan } from '@mediaingenuity/totallymoney.types'

type ComparatorKey =
    | 'apr'
    | 'recommenderScore'
    | 'recommenderScoreRanking'
    | 'loanId'
    | 'score'
    | 'approved'

type Comparator = (a: ScoredLoan, b: ScoredLoan) => number

const comparators: Record<ComparatorKey, Comparator> = {
    apr: (a, b) => {
        if (a.apr == null && b.apr != null) {
            return 1
        }
        if (a.apr !== null && b.apr == null) {
            return -1
        }
        if (a.apr == null && b.apr == null) {
            return 0
        }
        if (a.apr != null && b.apr != null) {
            return a.apr - b.apr
        }
        return 0
    },
    loanId: (a, b) => parseInt(a.loanId, 10) - parseInt(b.loanId, 10),
    // Note: for score and recommenderScore, null goes between 0 and positive
    score: (a, b) => {
        return a.score === null && b.score === 0
            ? -1
            : b.score === null && a.score === 0
              ? 1
              : b.score! - a.score!
    },
    recommenderScore: (a, b) => {
        // sort in descending order
        return b.recommenderScore - a.recommenderScore
    },
    recommenderScoreRanking: (a, b) => {
        // sort in ascending order
        return a.recommenderScore - b.recommenderScore
    },
    approved: (a, b) => {
        let diff = 0
        if (
            (a.scoreType === 'RateApproved' ||
                a.scoreType === 'CarPreApproved') &&
            a.score === 100
        ) {
            diff-- // eslint-disable-line no-plusplus
        }
        if (
            (b.scoreType === 'RateApproved' ||
                b.scoreType === 'CarPreApproved') &&
            b.score === 100
        ) {
            diff++ // eslint-disable-line no-plusplus
        }
        return diff
    },
}

export const sortKeys = [
    'recommenderScore',
    'score',
    'totalRepayment',
    'monthlyRepayment',
    'apr',
] as const

export type SortKey = (typeof sortKeys)[number]
export type SortKeyInternal = SortKey | 'recommenderScoreRanking'

const comparatorSequences: Record<SortKeyInternal, ComparatorKey[]> = {
    recommenderScore: ['recommenderScore', 'loanId'],
    recommenderScoreRanking: ['recommenderScoreRanking', 'loanId'],
    score: ['approved', 'score', 'apr', 'loanId'],
    totalRepayment: ['apr', 'score', 'loanId'],
    monthlyRepayment: ['apr', 'score', 'loanId'],
    apr: ['apr', 'score', 'loanId'],
}

export const defaultSortKey: SortKey = 'recommenderScore'

export const getSortComparator = (key: SortKeyInternal) => {
    // This will compare on multiple properties (as required) by recursively stepping through a sequence of comparators
    return function multiCompare(
        a: any,
        b: any,
        comparatorSequence: string[] = comparatorSequences[key]
    ): any {
        const comparatorKey = comparatorSequence ? comparatorSequence[0] : null

        if (comparatorKey) {
            return (
                (comparators as any)[comparatorKey](a, b) ||
                multiCompare(a, b, comparatorSequence.slice(1))
            )
        }

        return () => 0
    }
}
